import { useState, useEffect, useCallback, useRef } from 'react';

interface AudioPlayerState {
  isPlaying: boolean;
  currentAudio: string | null;
  isLoading: boolean;
  error: string | null;
}

export function useAudioPlayer() {
  const [state, setState] = useState<AudioPlayerState>({
    isPlaying: false,
    currentAudio: null,
    isLoading: false,
    error: null,
  });

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const preloadedAudios = useRef<Map<string, HTMLAudioElement>>(new Map());

  // Preload audio files
  const preloadAudio = useCallback((audioFiles: string[]) => {
    audioFiles.forEach(audioFile => {
      if (!preloadedAudios.current.has(audioFile)) {
        const audio = new Audio(audioFile);
        audio.preload = 'auto';
        
        // Handle loading events
        audio.addEventListener('canplaythrough', () => {
          console.log(`Audio preloaded: ${audioFile}`);
        });
        
        audio.addEventListener('error', (e) => {
          console.error(`Failed to preload audio: ${audioFile}`, e);
        });
        
        preloadedAudios.current.set(audioFile, audio);
      }
    });
  }, []);

  // Play audio
  const playAudio = useCallback(async (audioFile: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Stop current audio if playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      // Get preloaded audio or create new one
      let audio = preloadedAudios.current.get(audioFile);
      if (!audio) {
        audio = new Audio(audioFile);
        preloadedAudios.current.set(audioFile, audio);
      }

      audioRef.current = audio;
      audio.currentTime = 0;

      // Set up event listeners
      const handlePlay = () => {
        setState(prev => ({ 
          ...prev, 
          isPlaying: true, 
          currentAudio: audioFile, 
          isLoading: false 
        }));
      };

      const handleEnded = () => {
        setState(prev => ({ 
          ...prev, 
          isPlaying: false, 
          currentAudio: null 
        }));
      };

      const handleError = () => {
        setState(prev => ({ 
          ...prev, 
          isPlaying: false, 
          currentAudio: null, 
          isLoading: false,
          error: 'Failed to play audio' 
        }));
      };

      audio.addEventListener('play', handlePlay);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);

      // Play the audio
      await audio.play();

    } catch (error) {
      console.error('Error playing audio:', error);
      setState(prev => ({ 
        ...prev, 
        isPlaying: false, 
        currentAudio: null, 
        isLoading: false,
        error: 'Failed to play audio' 
      }));
    }
  }, []);

  // Stop audio
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setState(prev => ({ 
      ...prev, 
      isPlaying: false, 
      currentAudio: null 
    }));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      // Clean up preloaded audios
      preloadedAudios.current.forEach(audio => {
        audio.pause();
        audio.src = '';
      });
      preloadedAudios.current.clear();
    };
  }, []);

  return {
    ...state,
    playAudio,
    stopAudio,
    preloadAudio,
  };
}